// ============================================================
// PriorityMatrixView — the Eisenhower "Priority Matrix" view for the Quam
// Calendar workroom (/square/command-center/calendar, matrix mode).
//
// Additive, read-only. It consumes the SAME live CalItem[] the month grid uses
// (via useQuamCalendar in the workroom) and buckets tasks into the four
// importance × urgency quadrants using the replaceable classifyEisenhower
// policy. It owns no data and issues no writes — clicking an item defers to the
// workroom's existing EventWorkspace (onOpenItem), where the real per-record
// actions live. Time-anchored non-task items (events / calls / meetings /
// drafts / plans) have no priority axis and are shown honestly as a separate
// "Appointments" strip rather than forced into a quadrant.
//
// Named "Priority Matrix" (not "Decision Matrix") on purpose: the repo already
// owns "Decision Matrix" as a governed routing/complexity classifier and a
// Strategy MCDA card — this Eisenhower view must not masquerade as either.
//
// Inline styles with local palette constants that mirror @square/ui tokens
// (CLAUDE.md §6) — no CSS-in-JS, no stylesheet. SVG-free (text glyphs only).
// ============================================================

import { CAL_KIND_META, QUADRANT_META, QUADRANT_ORDER, URGENT_WINDOW_DAYS, groupByQuadrant, type CalItem, type Quadrant } from './calendarData'

/* palette — mirrors the Quam_Calendar_Final workroom tokens */
const w = '#edf2ff', w3 = '#5a6580'
const gld = '#c4a24e'
const mono = "'JetBrains Mono','SF Mono',monospace"

const priorityLabel: Record<string, string> = { urgent: 'URGENT', high: 'HIGH', normal: 'NORMAL', low: 'LOW' }

function ItemRow({ item, color, onOpenItem }: { item: CalItem; color: string; onOpenItem: (item: CalItem) => void }) {
  const tc = CAL_KIND_META[item.kind]
  const rawPriority = typeof (item.raw as Record<string, unknown>)?.priority === 'string'
    ? String((item.raw as Record<string, unknown>).priority)
    : null
  return (
    <button
      type="button"
      onClick={() => onOpenItem(item)}
      title={`${tc.label} — open the record for real actions`}
      style={{
        display: 'grid', gridTemplateColumns: '8px 1fr auto', gap: 10, alignItems: 'center',
        width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 10, marginBottom: 6,
        background: color + '08', border: '1px solid ' + color + '18', color: w, cursor: 'pointer',
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: tc.color, boxShadow: '0 0 6px ' + tc.color }} />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
        <span style={{ display: 'block', fontSize: 10.5, fontFamily: mono, color: w3, marginTop: 2 }}>
          {item.timeLabel || item.dateKey} · {tc.label}{item.status ? ` · ${String(item.status).replace(/_/g, ' ')}` : ''}
        </span>
      </span>
      {rawPriority && (
        <span style={{ fontSize: 8.5, fontFamily: mono, fontWeight: 800, letterSpacing: 1, color, opacity: 0.85 }}>{priorityLabel[rawPriority] || rawPriority.toUpperCase()}</span>
      )}
    </button>
  )
}

function QuadrantCard({ q, items, onOpenItem }: { q: Quadrant; items: CalItem[]; onOpenItem: (item: CalItem) => void }) {
  const meta = QUADRANT_META[q]
  return (
    <section
      aria-labelledby={`sqmx-${q}`}
      style={{
        padding: '16px 16px 12px', borderRadius: 16, minHeight: 150,
        background: meta.color + '06', border: '1px solid ' + meta.color + '20',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h2 id={`sqmx-${q}`} style={{ fontSize: 15, fontWeight: 500, color: meta.color, margin: 0 }}>{meta.label}</h2>
          <div style={{ fontSize: 9.5, fontFamily: mono, letterSpacing: 1, color: w3, marginTop: 2 }}>{meta.axis.toUpperCase()}</div>
        </div>
        <span style={{ fontSize: 13, fontFamily: mono, fontWeight: 700, color: items.length ? meta.color : w3 }}>{items.length}</span>
      </header>
      {items.length === 0
        ? <div style={{ fontSize: 12, color: w3, fontStyle: 'italic', padding: '6px 0' }}>No tasks classified here.</div>
        : items.map((item) => <ItemRow key={item.key} item={item} color={meta.color} onOpenItem={onOpenItem} />)}
    </section>
  )
}

export default function PriorityMatrixView({ items, onOpenItem, now }: { items: CalItem[]; onOpenItem: (item: CalItem) => void; now?: Date }) {
  const groups = groupByQuadrant(items, now)
  const taskCount = QUADRANT_ORDER.reduce((n, q) => n + groups[q].length, 0)

  return (
    <div style={{ animation: 'qcalFadeIn .3s ease' }}>
      {/* Honesty note: what the quadrants mean and that the policy is replaceable */}
      <p style={{ margin: '0 0 14px', fontSize: 10.5, fontFamily: mono, color: w3, lineHeight: 1.7 }}>
        Priority derived from each task&apos;s real priority + due date (urgent ≤ {URGENT_WINDOW_DAYS} days) — a
        replaceable prototype policy that never overrides your explicit choice. Only tasks carry priority; time-anchored
        items are listed under Appointments, not ranked.
      </p>

      {taskCount === 0 && (
        <div style={{ fontSize: 13, color: w3, fontStyle: 'italic', textAlign: 'center', padding: '4px 0 16px' }}>
          No due-dated tasks this month — the quadrants are genuinely empty (nothing invented).
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
        {QUADRANT_ORDER.map((q) => (
          <QuadrantCard key={q} q={q} items={groups[q]} onOpenItem={onOpenItem} />
        ))}
      </div>

      {groups.appointments.length > 0 && (
        <section aria-labelledby="sqmx-appointments" style={{ marginTop: 16, padding: '16px 16px 12px', borderRadius: 16, background: 'rgba(255,255,255,0.015)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <header style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <h2 id="sqmx-appointments" style={{ fontSize: 15, fontWeight: 500, color: w, margin: 0 }}>Appointments</h2>
              <div style={{ fontSize: 9.5, fontFamily: mono, letterSpacing: 1, color: w3, marginTop: 2 }}>TIME-ANCHORED · SHOWN BY SCHEDULE, NOT PRIORITY</div>
            </div>
            <span style={{ fontSize: 13, fontFamily: mono, fontWeight: 700, color: gld }}>{groups.appointments.length}</span>
          </header>
          {groups.appointments.map((item) => <ItemRow key={item.key} item={item} color={CAL_KIND_META[item.kind].color} onOpenItem={onOpenItem} />)}
        </section>
      )}
    </div>
  )
}
